import { FormikProvider, Formik, useFormik } from "formik";
import React, { useEffect, useState, useRef } from "react";
import { Button, Col, Form, Modal, Row } from "react-bootstrap";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { useIntl } from "react-intl";
import AutocompleteV2 from "../../component/AutocompleteObjectV2";
import OCTAutocomplete from "../../component/autocompleteOct"
import LabelRequired from "../../component/LabelRequired";
import TextField from "../../component/TextField";
import { IOrganization } from "../models/OrganizationModels";
import { getIndexedDBItem } from "../../utils/IndexedDB";
import { KEY_DS_DANH_MUC_TIEP_DON } from "../../utils/Constant";
import { IDanhSachDanhMuc } from '../../models/OctResponse';
import { searchDanhSachDV, addDV, updateDV } from "../services/services";
import { CODE, KEY } from '../../utils/Constant';

type Props = {
  handleClose: (isBackHomePage?: boolean) => void;
  rowData: IOrganization;
  handleReload: () => Promise<void>;
};

const OrganizationDetailDialog = (props: Props) => {
  const { handleClose, rowData, handleReload } = props;
  const intl = useIntl();
  const [initialValues, setInitialValues] = React.useState<IOrganization>({
    id: "",
    name: "",
    code: "",
    website: "",
    parentId: "",
    parentCode: "",
    provinceCode: "",
    provinceName: "",
    districtCode: "",
    districtName: "",
    subDistrictCode: "",
    subDistrictName: "",
    address: "",
    addressFull: "",
  });
  const [danhSachDanhMuc, setDanhSachDanhMuc] = useState<IDanhSachDanhMuc>();
  const [provinces, setProvinces] = useState<any>([]);
  const [districts, setDistricts] = useState<any>([]);
  const [subDistricts, setSubDistricts] = useState<any>([]);
  const formRef = useRef<any>(null);
  const validationSchema = Yup.object({});

  useEffect(() => {
    handleSetDefaultData();
  }, []);

  useEffect(() => {
    if (rowData?.id) {
      formRef?.current?.setValues(rowData);
    } else {
      formRef?.current?.resetForm();
    }
  }, [rowData]);

  useEffect(() => {
    if (rowData?.id && danhSachDanhMuc) {
      const province = rowData?.provinceCode ? danhSachDanhMuc?.provinces?.find(
        (item: any) => item?.code === rowData?.provinceCode
      ) : null;
      handleChangeAddress("province", province);
      const district = rowData?.districtCode ? danhSachDanhMuc?.districts?.find(
        (item: any) => item?.code === rowData?.districtCode
      ) : null;
      handleChangeAddress("district", district);
      const subDistricts = rowData?.subDistrictCode ? danhSachDanhMuc?.subDistricts?.find(
        (item: any) => item?.code === rowData?.subDistrictCode
      ) : null;
      handleChangeAddress("subDistricts", subDistricts);
    }
  }, [danhSachDanhMuc, rowData]);

  const handleSetDefaultData = async () => {
    try {
      let res: IDanhSachDanhMuc = await getIndexedDBItem(
        KEY_DS_DANH_MUC_TIEP_DON
      );
      setDanhSachDanhMuc(res || []);
      setProvinces(res?.provinces || []);
    } catch (error) {
      console.error(error);
    }
  };

  const handleSubmit = async (values: IOrganization) => {
    let _values = {
      ...values,
    };
    console.log(_values);
    
    try {
      const res = rowData?.id
        ? await updateDV(rowData?.id, _values)
        : await addDV(_values);
      if (res?.data?.code === CODE.SUCCESS) {
        toast.success(
          rowData.id
            ? intl.formatMessage({ id: "TOAST.EDIT.SUCCESS" })
            : intl.formatMessage({ id: "TOAST.CREATE.SUCCESS" })
        );
        handleReload();
        handleClose();
      } else {
        toast.error(`${res?.data?.message}`);
      }
    } catch (err) {
      toast.error(intl.formatMessage({ id: "GENERAL.ERROR" }));
    }
  };

  const formik = useFormik({
    initialValues: rowData?.id ? rowData : initialValues,
    validationSchema: validationSchema,
    onSubmit: handleSubmit,
  });

  const handleChange = (name: string, value: any) => {
    console.log(name, value);
    formik.setFieldValue(name, value);
  };

  const handleChangeAddress = (name: string, value: any) => {
    if (!value) return;
    console.log(name, value);
    switch (name) {
      case "province":
        formik.setFieldValue("provinceCode", value?.code);
        formik.setFieldValue("provinceName", value?.name);
        let district =
          danhSachDanhMuc?.districts?.filter(
            (item: any) => item?.provinceId === value?.id
          ) || [];
        console.log(district);
        setDistricts(district);
        break;
      case "district":
        formik.setFieldValue("districtCode", value?.code);
        formik.setFieldValue("districtName", value?.name);
        let subDistricts =
          danhSachDanhMuc?.subDistricts?.filter(
            (item: any) => item?.districtId === value?.id
          ) || [];
          console.log(subDistricts);
        setSubDistricts(subDistricts);
        break;
      case "subDistricts":
        formik.setFieldValue("subDistrictCode", value?.code);
        formik.setFieldValue("subDistrictName", value?.name);
        break;
      default:
        break;
    }
    formik.setFieldValue(name, value);
  }

  return (
    <Modal show={true} centered onHide={() => handleClose(false)} size="xl" >
      <FormikProvider value={formik}>
        <Form onSubmit={formik.handleSubmit}>
          <Modal.Header closeButton className="header-modal">
            <Modal.Title className="text-pri ">Thông tin đơn vị</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row className="mb-3">
              <Col lg={3}>
                <TextField
                  label={<LabelRequired isRequired label="Tên đơn vị" />}
                  name={"name"}
                  type={"text"}
                  value={formik.values?.name}
                />
              </Col>

              <Col lg={3}>
                <TextField
                  label={<LabelRequired isRequired label="Mã đơn vị" />}
                  name={"code"}
                  type={"text"}
                  value={formik.values?.code}
                />
              </Col>

              <Col lg={3}>
                <TextField
                  label={<LabelRequired label="Website" />}
                  name={"website"}
                  type={"text"}
                  value={formik.values?.website}
                />
              </Col>

              <Col lg={3}>
                <TextField
                  label={<LabelRequired label="Địa chỉ chi tiết" />}
                  name={"address"}
                  type={"text"}
                  value={formik.values?.address}
                />
              </Col>
            </Row>

            <Row>
              <Col lg={3}>
                <LabelRequired label="Tỉnh/ Thành phố" />
                <AutocompleteV2
                  options={provinces}
                  getOptionLabel={(option: any) => `${option?.name}`}
                  value={formik.values?.provinceCode}
                  isSearchDefauilt={true}
                  name={"province"}
                  onChange={(options: any) =>
                    handleChangeAddress("province", options)
                  }
                  className="autocomplete-custom-renderform radius spaces min-w-200 h-29"
                // errors={errors?.province}
                // touched={touched?.province}
                />
              </Col>

              <Col lg={3}>
                <LabelRequired label="Quận/ Huyện" />
                <AutocompleteV2
                  options={districts}
                  getOptionLabel={(option: any) => `${option?.name}`}
                  value={formik.values?.districtCode}
                  isSearchDefauilt={true}
                  name={"district"}
                  onChange={(options: any) =>
                    handleChangeAddress("district", options)
                  }
                  className="autocomplete-custom-renderform radius spaces min-w-200 h-29"
                // errors={errors?.district}
                // touched={touched?.district}
                />
              </Col>

              <Col lg={3}>
                <LabelRequired label="Xã/ Phường" />
                <AutocompleteV2
                  options={subDistricts}
                  getOptionLabel={(option: any) => `${option?.name}`}
                  value={formik.values?.subDistrictCode}
                  isSearchDefauilt={true}
                  name={"subDistricts"}
                  onChange={(options: any) =>
                    handleChangeAddress("subDistricts", options)
                  }
                  className="autocomplete-custom-renderform radius spaces min-w-200 h-29"
                // errors={errors?.ward}
                // touched={touched?.ward}
                />
              </Col>

              <Col lg={3}>
                <LabelRequired label="Đơn vị cha" />
                <OCTAutocomplete
                  // lable={"Đơn vị cha"}
                  options={[]}
                  value={formik.values?.parentId}
                  name="parentId"
                  valueSearch="id"
                  onChange={(selectedOption: any) => {
                    handleChange(
                      "parentId",
                      selectedOption?.id
                    );
                    handleChange(
                      "parentCode",
                      selectedOption?.code
                    );
                  }}
                  getOptionValue={(option) => option.deptDepartmentId}
                  searchFunction={searchDanhSachDV}
                  searchObject={{
                    pageSize: 999,
                    pageIndex: 1,
                  }}
                  styles={{
                    paddingTop: "0px !important",
                  }}
                  errors={formik.errors.parentId}
                  touched={formik.touched.parentId}
                />
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button
              type="submit"
              className="btn-fill mx-3 spaces min-w-90px btn btn-primary"
            >
              Lưu
            </Button>
            <Button
              variant="btn-fill spaces mx-3 min-w-90px btn btn-secondary"
              onClick={() => handleClose(false)}
            >
              Hủy
            </Button>
          </Modal.Footer>
        </Form>
      </FormikProvider>
    </Modal>
  );
};

export default OrganizationDetailDialog;
